import type { Unit, UnitPlace, UnitStatus } from "./api";

/** Rule A-7: the status rules of a container unit. The API has the same table. */
export const MOVES: Record<UnitStatus, UnitStatus[]> = {
  available: ["staged", "loaded", "reconditioning", "retired"],
  staged: ["available", "loaded"],
  loaded: ["available", "deployed"],
  deployed: ["reclaimed"],
  reclaimed: ["available", "reconditioning", "retired"],
  reconditioning: ["available", "retired"],
  retired: [],
};

export const PLACES: Record<UnitStatus, UnitPlace[]> = {
  available: ["hub"],
  staged: ["hub"],
  loaded: ["truck"],
  deployed: ["account"],
  reclaimed: ["truck", "hub"],
  reconditioning: ["hub"],
  retired: ["hub"],
};

export const STATUS_TEXT: Record<UnitStatus, string> = {
  available: "Available",
  staged: "Staged",
  loaded: "Loaded",
  deployed: "At an account",
  reclaimed: "Picked up",
  reconditioning: "Reconditioning",
  retired: "Retired",
};

/** The usual next steps first, and Retired last, because Retired is final. */
const STEP_ORDER: UnitStatus[] = ["available", "staged", "loaded", "deployed", "reclaimed", "reconditioning", "retired"];

export const nextInOrder = (next: readonly UnitStatus[]): UnitStatus[] =>
  [...next].sort((a, b) => STEP_ORDER.indexOf(a) - STEP_ORDER.indexOf(b));

export const SERIAL_MAX = 40;

export function serialLengthText(serial: string): string | null {
  const length = serial.trim().length;
  return length > SERIAL_MAX ? `A serial number is ${SERIAL_MAX} characters at most (now ${length}).` : null;
}

export const PLACE_TEXT: Record<UnitPlace, string> = { hub: "Yard", truck: "Truck", account: "Account" };

export const canMove = (from: UnitStatus, to: UnitStatus, place: UnitPlace) => MOVES[from].includes(to) && PLACES[to].includes(place);

/** The place of a unit in words. An account that the role cannot see shows as "An account". */
export function whereText(unit: Pick<Unit, "place" | "hub_name" | "truck" | "account_number" | "account_name">): string {
  if (unit.place === "truck") return `Truck ${unit.truck ?? ""}`.trim();
  if (unit.place === "account") return unit.account_number ? `${unit.account_number} ${unit.account_name ?? ""}`.trim() : "An account";
  return unit.hub_name ?? "Yard";
}

// See RANGE_MAX in platform/api/app/units.py.
export const RANGE_MAX = 10_000;

/** The serial numbers of a range, as the API makes them, or a problem in words. */
export function rangeSerials(prefix: string, first: string, last: string, max = RANGE_MAX): string[] | string {
  if (!/^\d+$/.test(first) || !/^\d+$/.test(last)) return "The first and the last number must be digits.";
  const start = Number(first);
  const end = Number(last);
  if (end < start) return "The last number must not be less than the first number.";
  if (end - start + 1 > max) return `Add at most ${max} units at one time.`;
  return Array.from({ length: end - start + 1 }, (_, i) => `${prefix.trim()}${String(start + i).padStart(first.length, "0")}`);
}

export const sizeText = (size: number) => `${size} gal`;
